export type CanvasGaugeOptions = {
  startAngle: number;
  endAngle: number;
  clockwise: boolean;
  min: number;
  max: number;
  lineWidth: number;
  value: number;
  // 可选：展示层量化步长（presentation quantization），默认由使用方决定。
  displayQuantStep?: number;
  // 可选：最小重定向间隔（ms），用于合并高频更新
  minRetargetMs?: number;
};

export type LegacyEchartsGaugeSeries = {
  startAngle?: number;
  endAngle?: number;
  clockwise?: boolean;
  min?: number;
  max?: number;
  axisLine?: { lineStyle?: { width?: number } };
  data?: Array<{ value?: number }>;
};

export type LegacyEchartsGaugeOptions = {
  series?: LegacyEchartsGaugeSeries[];
};

export type DashboardOptions =
  | Partial<CanvasGaugeOptions>
  | LegacyEchartsGaugeOptions;

export const degToRad = (deg: number) => (deg * Math.PI) / 180;

// ECharts gauge 的角度以“数学坐标系(0=右, 90=上)”定义；Canvas y 轴向下，需要取反
export const echartsDegToCanvasRad = (deg: number) => degToRad(-deg);

export const normalizeSweepDeg = (
  startAngle: number,
  endAngle: number,
  clockwise: boolean
) => {
  let sweep = clockwise ? startAngle - endAngle : endAngle - startAngle;
  // 归一化到 (0, 360]，同时支持 endAngle=-270 这类值
  sweep = ((sweep % 360) + 360) % 360;
  if (sweep === 0) sweep = 360;
  return sweep;
};

export function toCanvasGaugeOptions(options: DashboardOptions | undefined) {
  if (!options) return null;

  // Canvas 轻量对象
  if (
    typeof (options as any)?.startAngle === 'number' &&
    typeof (options as any)?.endAngle === 'number' &&
    typeof (options as any)?.clockwise === 'boolean'
  ) {
    const o = options as Partial<CanvasGaugeOptions>;
    return {
      startAngle: o.startAngle ?? 90,
      endAngle: o.endAngle ?? -270,
      clockwise: !!o.clockwise,
      min: typeof o.min === 'number' ? o.min : 0,
      max: typeof o.max === 'number' ? o.max : 240,
      lineWidth: typeof o.lineWidth === 'number' ? o.lineWidth : 20,
      value: typeof o.value === 'number' ? o.value : 0,
      displayQuantStep:
        typeof o.displayQuantStep === 'number' ? o.displayQuantStep : undefined,
      minRetargetMs:
        typeof o.minRetargetMs === 'number' ? o.minRetargetMs : undefined,
    } satisfies CanvasGaugeOptions;
  }

  // 旧 ECharts options
  const legacy = options as LegacyEchartsGaugeOptions;
  const s0 = legacy?.series?.[0];
  const val = s0?.data?.[0]?.value;
  return {
    startAngle: typeof s0?.startAngle === 'number' ? s0.startAngle : 90,
    endAngle: typeof s0?.endAngle === 'number' ? s0.endAngle : -270,
    clockwise: !!s0?.clockwise,
    min: typeof s0?.min === 'number' ? s0.min : 0,
    max: typeof s0?.max === 'number' ? s0.max : 240,
    lineWidth:
      typeof s0?.axisLine?.lineStyle?.width === 'number'
        ? s0.axisLine.lineStyle.width
        : 20,
    value: typeof val === 'number' ? val : 0,
  } satisfies CanvasGaugeOptions;
}
